import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import type { AttributionSource } from '@kilat/shared';
import { ghostLinkStyle, Surface } from '@kilat/ui';

import { useI18n } from '../lib/use-i18n.js';

/**
 * "Where new customers came from" — paid washes booked through a Share-kit
 * link, by the channel it was shared on (Share & grow).
 *
 * Fed by the `bySource` slice of the daily stats rollup, so a 30-day range is
 * the same ≤ 30 small documents `/reports` already reads — no ticket scan.
 *
 * Counts only bookings that arrived through a tracked link. A walk-in, or a
 * regular who opened the app from their home screen, has no source and is not
 * counted as one; the card says what it measures rather than implying it
 * explains every customer.
 */
export interface SourceRow {
  readonly source: AttributionSource;
  readonly count: number;
}

export function AttributionSourcesCard({ rows }: { readonly rows: readonly SourceRow[] }) {
  const { t } = useI18n();
  const max = rows[0]?.count ?? 1;

  return (
    <Surface as="section" aria-labelledby="sources-title">
      <header style={{ marginBottom: 'var(--space-4)' }}>
        <h2 id="sources-title" style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t('dashboard.reports.sources.title')}
        </h2>
        <p style={mutedStyle}>{t('dashboard.reports.sources.subtitle')}</p>
      </header>

      {rows.length === 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'start' }}>
          <p style={{ ...mutedStyle, marginTop: 0 }}>{t('dashboard.reports.sources.emptyBody')}</p>
          <Link to="/marketing/share" style={ghostLinkStyle}>
            {t('dashboard.reports.sources.emptyCta')}
          </Link>
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          {rows.map((r) => (
            <li key={r.source}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  {t(`dashboard.reports.sources.source.${r.source}`)}
                </span>
                <span style={{ fontSize: 'var(--text-meta)', fontVariantNumeric: 'tabular-nums' }}>
                  {t(
                    r.count === 1
                      ? 'dashboard.reports.sources.washesOne'
                      : 'dashboard.reports.sources.washesOther',
                    { count: r.count },
                  )}
                </span>
              </div>
              <div aria-hidden style={trackStyle}>
                <div
                  style={{
                    ...barStyle,
                    width: `${Math.max(2, (r.count / Math.max(1, max)) * 100)}%`,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

const mutedStyle: CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const trackStyle: CSSProperties = {
  background: 'var(--tint-subtle)',
  borderRadius: 'var(--radius-sm)',
  height: 'var(--space-2)',
  overflow: 'hidden',
};

const barStyle: CSSProperties = {
  height: '100%',
  background: 'var(--color-primary)',
  borderRadius: 'var(--radius-sm)',
};
